import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  type RatingPoint,
  getRatingExtent,
  formatRatingTime,
} from '@/lib/ratingGraph';

interface RatingGraphProps {
  /** Pre-calculated trajectory points (from calculateRatingTrajectory) */
  data: RatingPoint[];
  /** SVG height in pixels */
  height?: number;
  /** Optional className for the wrapper */
  className?: string;
  /** Show data points on hover */
  showPoints?: boolean;
}

/**
 * RatingGraph - lightweight SVG line chart for FTPES trajectory.
 *
 * No external chart library dependency. Renders a responsive SVG with:
 * - Grid lines
 * - Smooth line (catmull-rom spline via cubic bezier approximation)
 * - Data points on hover
 * - Tooltip with match details
 * - Axis labels
 *
 * The component is purely presentational; all data transformation happens
 * in the pure `calculateRatingTrajectory` function (lib/ratingGraph.ts)
 * which is unit tested in packages/core.
 */
export function RatingGraph({
  data,
  height = 300,
  className,
  showPoints = true,
}: RatingGraphProps) {
  if (data.length < 2) {
    return (
      <div
        className={cn('flex items-center justify-center h-full', className)}
        style={{ height }}
      >
        <div className="text-center text-muted-foreground">
          <p className="font-medium">Insufficient data for graph</p>
          <p className="text-sm">Play at least 2 matches to see your trajectory</p>
        </div>
      </div>
    );
  }

  const [minRating, maxRating] = getRatingExtent(data);
  const width = typeof window !== 'undefined' ? window.innerWidth : 800;
  const padding = { top: 24, right: 48, bottom: 40, left: 60 };
  const innerWidth = Math.max(400, width - padding.left - padding.right);
  const innerHeight = height - padding.top - padding.bottom;

  // Scale functions
  const xScale = (index: number) =>
    padding.left + (index / (data.length - 1)) * innerWidth;
  const yScale = (rating: number) =>
    padding.top +
    innerHeight -
    ((rating - minRating) / (maxRating - minRating)) * innerHeight;

  // Catmull-Rom spline -> cubic bezier approximation for smooth curve
  function getSmoothPath(points: { x: number; y: number }[]): string {
    if (points.length < 2) return '';
    let path = `M ${points[0]!.x} ${points[0]!.y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)]!;
      const p1 = points[i]!;
      const p2 = points[i + 1]!;
      const p3 = points[Math.min(points.length - 1, i + 2)]!;

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      path += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p2.x} ${p2.y}`;
    }
    return path;
  }

  const plotPoints = data.map((d, i) => ({
    x: xScale(i),
    y: yScale(d.rating),
    ...d,
  }));

  const pathData = getSmoothPath(plotPoints);

  // Grid lines
  const yTicks = 5;
  const gridLines = Array.from({ length: yTicks + 1 }, (_, i) => {
    const value = maxRating - (i / yTicks) * (maxRating - minRating);
    const y = yScale(value);
    return { y, value: Math.round(value) };
  });

  const xTicks = Math.min(data.length, 6);
  const xGridLines = Array.from({ length: xTicks }, (_, i) => {
    const idx = Math.round((i / (xTicks - 1)) * (data.length - 1));
    const point = data[idx];
    return { x: xScale(idx), label: point ? formatRatingTime(point.timestamp) : '' };
  });

  // Hover state
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <div className={cn('relative', className)} style={{ height, width: '100%' }}>
      <svg
        viewBox={`0 0 ${innerWidth + padding.left + padding.right} ${height}`}
        preserveAspectRatio="none"
        className="w-full h-full text-muted-foreground"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {/* Background */}
        <rect
          x={padding.left}
          y={padding.top}
          width={innerWidth}
          height={innerHeight}
          fill="transparent"
        />

        {/* Horizontal grid lines */}
        {gridLines.map(({ y, value }) => (
          <g key={value}>
            <line
              x1={padding.left}
              y1={y}
              x2={padding.left + innerWidth}
              y2={y}
              stroke="currentColor"
              strokeWidth="0.5"
              opacity="0.1"
              strokeDasharray="4 4"
            />
            <text
              x={padding.left - 8}
              y={y + 4}
              textAnchor="end"
              fontSize="11"
              fill="currentColor"
              opacity="0.6"
            >
              {value}
            </text>
          </g>
        ))}

        {/* Vertical grid lines + x-axis labels */}
        {xGridLines.map(({ x, label }) => (
          <g key={x}>
            <line
              x1={x}
              y1={padding.top}
              x2={x}
              y2={padding.top + innerHeight}
              stroke="currentColor"
              strokeWidth="0.5"
              opacity="0.1"
              strokeDasharray="4 4"
            />
            <text
              x={x}
              y={padding.top + innerHeight + 18}
              textAnchor="middle"
              fontSize="10"
              fill="currentColor"
              opacity="0.7"
            >
              {label}
            </text>
          </g>
        ))}

        {/* Area under line (gradient fill) */}
        <defs>
          <linearGradient id="rating-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.15" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d={`${pathData} L ${plotPoints[plotPoints.length - 1]!.x} ${padding.top + innerHeight} L ${plotPoints[0]!.x} ${padding.top + innerHeight} Z`}
          fill="url(#rating-gradient)"
        />

        {/* Rating line */}
        <path
          d={pathData}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="drop-shadow-[0_2px_8px_hsl(var(--primary)/0.3)]"
        />

        {/* Data points on hover */}
        {showPoints && hoveredIndex !== null && (
          <g>
            {plotPoints.map((p, i) => (
              <g
                key={p.matchId}
                transform={`translate(${p.x}, ${p.y})`}
                style={{
                  opacity: i === hoveredIndex ? 1 : 0,
                  transition: 'opacity 0.15s',
                }}
              >
                <circle r={5} fill="hsl(var(--background))" stroke="hsl(var(--primary))" strokeWidth={2.5} />
                <circle r={3} fill="hsl(var(--primary))" />
              </g>
            ))}
          </g>
        )}

        {/* Hover detection area (transparent rect per point) */}
        <g>
          {plotPoints.map((p, i) => (
            <rect
              key={p.matchId}
              x={p.x - 12}
              y={padding.top}
              width={24}
              height={innerHeight}
              fill="transparent"
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            />
          ))}
        </g>
      </svg>

      {/* Custom tooltip */}
      {(() => {
        if (hoveredIndex === null) return null;
        const hp = plotPoints[hoveredIndex]!;
        const hd = data[hoveredIndex]!;
        return (
          <div
            className="absolute z-10 pointer-events-none"
            style={{
              left: hp.x + padding.left + 12,
              top: hp.y + padding.top - 8,
              transform: 'translateX(-50%) translateY(-100%)',
            }}
          >
            <div className="rounded-lg bg-popover text-popover-foreground border border-border p-3 shadow-lg min-w-[180px]">
              <p className="text-xs text-muted-foreground">
                {formatRatingTime(hd.timestamp)}
              </p>
              <p className="text-lg font-bold font-mono text-primary">
                {hd.rating} FTPES
              </p>
              <p className={cn(
                'text-sm font-medium mt-1',
                hd.result === 'win' && 'text-emerald-600',
                hd.result === 'loss' && 'text-rose-600',
                hd.result === 'draw' && 'text-amber-600',
              )}>
                {hd.result === 'win' && 'Win'}
                {hd.result === 'loss' && 'Loss'}
                {hd.result === 'draw' && 'Draw'}
                {' '}
                ({hd.delta > 0 ? '+' : ''}{hd.delta})
              </p>
            </div>
          </div>
        );
      })()}
    </div>
  );
}